import React, { PropsWithChildren, useContext, useRef } from "react"
import { ConfigContext, CustomFieldContext } from "../contexts/CustomConfigContext"
import * as Bs from "react-bootstrap"
import { useTranslationOrUndefined } from "../i18n"
import { Field } from "../models/CustomConfig"

export type FormGroupProps = PropsWithChildren<{
    label: string
    labelClassName?: string
    inline?: boolean
}>

export function FormGroup(props: FormGroupProps) {
    const id = useRef(
        Math.random()
            .toString(36)
            .replace(/[^a-z]+/g, ""),
    )

    const fieldCustom = useCustomConfigForField(props.label)
    const tNullSafe = useTranslationOrUndefined()

    if (fieldCustom?.hide) return // don't render hidden fields

    const tooltipText = tNullSafe(fieldCustom?.hint)
    const tooltip = tooltipText && <Bs.Tooltip id={id + "_tooltip"}>{tooltipText}</Bs.Tooltip>

    let translatedLabel = tNullSafe(fieldCustom?.label) ?? props.label
    if (fieldCustom?.category) {
        translatedLabel = translatedLabel.replaceAll("*", "") + ` ${fieldCustom.category}`
    }

    // Wenn custom Required gesetzt, nutze das, sonst prüfe auf einen Stern am Ende des übersetzen Textes
    const fieldCustomExtraClass = fieldCustom?.required ?? translatedLabel.endsWith(" *") ? "required" : ""

    const innerElement = props.inline ? (
        <div className="form-group">
            <label htmlFor={id.current} className={`col-form-label ${props.labelClassName} ${fieldCustomExtraClass}`}>
                {translatedLabel}
            </label>

            {React.Children.map(props.children, (child) =>
                React.cloneElement(child as React.ReactElement<any>, {
                    id: id.current,
                }),
            )}
        </div>
    ) : (
        <div className="form-group row">
            <label htmlFor={id.current} className={`col-md-3 col-form-label ${fieldCustomExtraClass}`}>
                {translatedLabel}
            </label>

            <div className="col-md-9">
                {React.Children.map(props.children, (child) =>
                    React.cloneElement(child as React.ReactElement<any>, {
                        id: id.current,
                    }),
                )}
            </div>
        </div>
    )

    return (
        <CustomFieldContext.Provider value={fieldCustom || null}>
            <Bs.OverlayTrigger overlay={tooltip || <div />} trigger="focus">
                {innerElement}
            </Bs.OverlayTrigger>
        </CustomFieldContext.Provider>
    )
}

function useCustomConfigForField(labelText: string): Field | undefined {
    return useContext(ConfigContext)?.fields?.filter((it) => {
        const fieldIdWithBraces = `(${it.id})`.toLowerCase()
        return labelText.toLowerCase().indexOf(fieldIdWithBraces) >= 0
    })[0]
}
